import { Card, Container, Divider, Link, Typography } from "@mui/material";
import { Box } from "@mui/system";
import type { NextPage } from "next";
import Head from "next/head";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { AmplifyLogin } from "src/components/authentication/amplify-login";
import { GuestGuard } from "src/components/authentication/guest-guard";
import { Logo } from "src/components/logo";
import { useAuth } from "../../hooks/use-auth";
import { gtm } from "../../lib/gtm";

const Login: NextPage = () => {
  const router = useRouter();
  const { platform, user, isAuthenticated } = useAuth() as any;
  const { disableGuard } = router.query;
  const [returnUrl, setReturnUrl] = useState<string | null>(null);

  // First useEffect: set returnUrl from query or localStorage
  useEffect(() => {
    gtm.push({ event: "page_view" });

    // Set returnUrl from query string if present
    if (
      router?.query?.returnUrl &&
      router?.query?.returnUrl !== "null" &&
      router?.query?.returnUrl !== "undefined" &&
      typeof router?.query?.returnUrl === "string"
    ) {
      setReturnUrl(router?.query?.returnUrl);
    }

    if (typeof window !== "undefined") {
      let storedReturnUrl = window.localStorage.getItem("returnUrl");
      if (
        storedReturnUrl &&
        storedReturnUrl !== "null" &&
        storedReturnUrl !== "undefined"
      ) {
        window.localStorage.removeItem("returnUrl");
        router.push(`?returnUrl=${storedReturnUrl}`);
      }
    }
  }, [router?.query?.returnUrl]);

  return (
    <>
      <Head>
        <title>Login | SEOwind.io</title>
      </Head>
      <Box
        component="main"
        sx={{
          backgroundColor: "background.default",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            py: {
              xs: "60px",
              md: "120px",
            },
          }}
        >
          <Card elevation={16} sx={{ p: 4 }}>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <NextLink href="https://seowind.io" passHref>
                <a>
                  <Logo
                    sx={{
                      height: 31,
                      width: 148,
                    }}
                  />
                </a>
              </NextLink>
              <Typography variant="h4" sx={{ pt: 2 }}>
                Sign in to your account
              </Typography>
            </Box>
            <Box
              sx={{
                flexGrow: 1,
                mt: 3,
              }}
            >
              {platform === "Amplify" && <AmplifyLogin />}
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <NextLink
                href={
                  disableGuard
                    ? `/authentication/register?disableGuard=${disableGuard}`
                    : returnUrl
                    ? `/authentication/register?returnUrl=${returnUrl}`
                    : "/authentication/register"
                }
                passHref
              >
                <Link color="textSecondary" variant="body2">
                  New user? Get started here
                </Link>
              </NextLink>
              <NextLink
                href={
                  disableGuard
                    ? `/authentication/password-recovery?disableGuard=${disableGuard}`
                    : "/authentication/password-recovery"
                }
                passHref
              >
                <Link color="textSecondary" variant="body2">
                  Forgot password
                </Link>
              </NextLink>
            </Box>
          </Card>
        </Container>
      </Box>
    </>
  );
};

Login.getLayout = (page) => <GuestGuard>{page}</GuestGuard>;

export default Login;
